import { useState } from "react";
import { bool, string, func } from "prop-types";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import * as yup from "yup";
import { MAX_ESTIMATE_TITLE_LENGTH } from "@utils";
import { ControlledTextField } from "./index";
import { ClickAwayListener, IconButton, InputAdornment } from "@material-ui/core";
import { Clear } from "@material-ui/icons";
import classnames from "classnames";
import { makeStyles } from "@material-ui/styles";

export const EstimateNameEditor = ({
  name,
  onSubmit,
  useH1,
  className,
  formClassName,
  allowEdition = true,
}) => {
  const { t } = useTranslation();
  const classes = useStyle();

  const [ isEditable, setIsEditable ] = useState(false);
  
  const {
    control,
    handleSubmit,
    reset,
  } = useForm({
    resolver: yupResolver(
      yup.object().shape({
        name: yup
          .string()
          .required(t("errors.project.name.required"))
          .min(4, t("errors.project.name.min"))
          .max(MAX_ESTIMATE_TITLE_LENGTH, t("errors.project.name.max"))
      })
    ),
    defaultValues: { name },
  });

  const handleDescriptionFocus = () => {
    if (allowEdition)
      setIsEditable(true);
  };
  
  const handleInputBlur = () => {
    setIsEditable(false);
    reset();
  }

  const submitHandler = async (data) => {
    if (!data.name.length) return;
    if (name !== data.name) {
      const newName = data.name.trim();
      onSubmit(newName);
    }
    setIsEditable(false);
  };
  
  const inactiveProps = {
    className: classnames("preset-title cursor-text ellipsis", className),
    onClick: handleDescriptionFocus,
  }

  return isEditable
    ? (
      <form
        onSubmit={handleSubmit(submitHandler)}
        aria-label="project name edition form"
        className={classnames("w-full -mx-2", formClassName)}
      >
        <ClickAwayListener mouseEvent="onMouseDown" onClickAway={handleSubmit(submitHandler)}>
          <ControlledTextField
            control={control}
            name="name"
            placeholder={t("views.active.popup.name")}
            autoFocus
            className="mb-0"
            InputProps={{
              inputProps: {
                className: classnames(classes.py3, "px-2", useH1 ? "text-h1" : "text-h2")
              },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    color="primary"
                    tabIndex={ -1 }
                    onClick={ handleInputBlur }
                    onMouseDown={( e ) => { e.preventDefault() }}
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </ClickAwayListener>
      </form>
    )
    : (
      useH1
        ? <h1 {...inactiveProps}>{ name }</h1>
        : <h2 { ...inactiveProps }>{ name }</h2>
  );
};

EstimateNameEditor.propTypes = {
  onSubmit: func.isRequired,
  name: string.isRequired,
  className: string,
  formClassName: string,
  allowEdition: bool,
  useH1: bool,
};

const useStyle = makeStyles(() => ({
  py3: {
    paddingTop: 3,
    paddingBottom: 3,
  },
}));